import { useEffect, useState } from "react";
import { passwordProblem, stretchPassword } from "@timecapsule/crypto/password";
import { api, type MailLinkInfo } from "../api";
import { Footer, PublicHeader } from "../App";
import { errorText, useT } from "../i18n";
import { Loading } from "../Loading";
import { queryParam } from "../router";
import { useRefreshHandler } from "../Refresh";

/**
 * Target of every mail link (/verify?token=...): confirms a registration or an added password, or
 * sets a new password. The link is used only by a click here (POST), never by opening it, so mail
 * scanners that fetch links cannot use them up.
 */
export function VerifyEmail() {
  const t = useT();
  const token = queryParam("token") ?? "";
  const [info, setInfo] = useState<MailLinkInfo | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  useRefreshHandler(async () => { const fresh = await api.mailLinkInfo(token); setInfo(fresh); setError(null); });

  useEffect(() => {
    api.mailLinkInfo(token).then(setInfo, (err: unknown) => {
      setInfo(null);
      setError(errorText(err));
    });
  }, [token]);

  const reset = info?.purpose === "reset";
  const problem = password.length === 0 ? null : passwordProblem(password);
  const ready = !reset || (password.length > 0 && !problem && password === repeat);

  async function confirm(e: React.FormEvent) {
    e.preventDefault();
    if (!info) return;
    setError(null);
    setBusy(true);
    try {
      const result=await api.useMailLink(token, reset ? await stretchPassword(password, info.email) : undefined);
      location.href = result.purpose==='recover'?'/recover-access':'/';
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <>
      <PublicHeader />
      <main className="narrow">
      {info === undefined && <Loading className="muted" />}
      {info && (
        <form onSubmit={confirm}>
          <h2>{t.verify.title[info.purpose]}</h2>
          <p>{t.verify.text[info.purpose](info.email)}</p>
          {reset && (
            <>
              <label>
                {t.login.password.newPassword}
                <input type="password" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
              </label>
              <label>
                {t.login.password.repeat}
                <input type="password" autoComplete="new-password" value={repeat} onChange={(e) => setRepeat(e.target.value)} />
              </label>
              {problem && <p className="error small">{t.login.password.rule}</p>}
              {repeat.length > 0 && password !== repeat && <p className="error small">{t.login.password.differ}</p>}
            </>
          )}
          <button className="primary" type="submit" disabled={!ready || busy}>
            {busy ? t.login.password.busy : t.verify.confirm[info.purpose]}
          </button>
        </form>
      )}
      {error && <p className="error">{error}</p>}
      {info === null && (
        <p>
          <a href="/">{t.verify.home}</a>
        </p>
      )}
      </main>
      <Footer />
    </>
  );
}
